import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { company, stats, helpBlocks } from "../mock";

function Reveal({ children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const x = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);
  const xOpp = useTransform(scrollYProgress, [0, 1], ["-15%", "5%"]);

  return (
    <section id="about" ref={ref} className="relative border-t border-white/5 py-28 md:py-40">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <Reveal>
          <span className="font-display text-xs uppercase tracking-[0.3em] text-[#d4ff4e]">
            (About)
          </span>
        </Reveal>

        <div className="mt-10 grid gap-12 md:grid-cols-12">
          <div className="md:col-span-7">
            <Reveal>
              <h2 className="font-display text-4xl font-medium leading-tight md:text-6xl">
                Hey! We're <span className="text-[#d4ff4e]">{company.name}</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 max-w-2xl text-lg leading-relaxed text-[#c9c9c4] md:text-xl">
                {company.intro}
              </p>
            </Reveal>
          </div>

          <div className="grid grid-cols-2 gap-6 md:col-span-5">
            {stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.08}>
                <div className="border-l border-white/10 pl-4">
                  <div className="font-display text-4xl font-bold text-white md:text-5xl">{s.value}</div>
                  <div className="mt-1 text-sm text-[#8a8a86]">{s.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      {/* Big scrolling statement - two directions */}
      <div className="mt-28 overflow-hidden md:mt-40">
        <motion.h3
          style={{ x }}
          className="font-display whitespace-nowrap text-[13vw] font-bold uppercase leading-none text-white/90"
        >
          We remove the friction
        </motion.h3>
        <motion.h3
          style={{ x: xOpp }}
          className="font-display whitespace-nowrap text-right text-[13vw] font-bold uppercase leading-none text-transparent [-webkit-text-stroke:1px_rgba(212,255,78,0.7)]"
        >
          between idea &amp; code.
        </motion.h3>
      </div>

      {/* Help blocks */}
      <div className="mx-auto mt-28 max-w-[1600px] px-6 md:mt-40 md:px-12">
        <Reveal>
          <h3 className="font-display mb-14 text-3xl font-medium md:text-4xl">How can we help?</h3>
        </Reveal>
        <div className="grid gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/5 md:grid-cols-3">
          {helpBlocks.map((b, i) => (
            <Reveal key={b.title} delay={i * 0.1}>
              <div className="group h-full bg-[#0a0a0b] p-8 transition-colors hover:bg-[#101012]">
                <span className="font-display text-sm text-[#d4ff4e]">0{i + 1}</span>
                <h4 className="font-display mt-4 text-xl font-medium text-white">{b.title}</h4>
                <p className="mt-4 text-sm leading-relaxed text-[#9a9a96]">{b.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
